import React, { useEffect, useMemo, useState, useRef } from "react";
import apiFetch from "./utils/api";
import Typeahead from "./components/Typeahead";
import "./ReportsPage.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Pie, Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
} from "chart.js";

import { getColorForKey } from "./utils/colors";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
);

ModuleRegistry.registerModules([AllCommunityModule]);

const ProjectOverview = ({ onClose } = {}) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectsMeta, setProjectsMeta] = useState([]);

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const HOURS_PER_DAY = 7.5;
  const [subcodeBreakdown, setSubcodeBreakdown] = useState(null);
  const [resourceSubcodeMatrix, setResourceSubcodeMatrix] = useState(null);
  const [projectMeta, setProjectMeta] = useState(null);
  const [projectYears, setProjectYears] = useState([]);
  const [drillRows, setDrillRows] = useState([]);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillTitle, setDrillTitle] = useState("");
  const gridRef = useRef(null);
  const pieRef = useRef(null);
  const barRef = useRef(null);

  const fetchReport = async (y) => {
    setLoading(true);
    setError("");
    try {
      const body = await apiFetch(`/api/reports/project-overview?year=${y}`);
      console.log(body);
      setMonths(body.months || []);
      setRows(body.data || []);
    } catch (e) {
      setError(e.message || "Failed to load Client Support");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(year);
  }, [year]);

  useEffect(() => {
    (async () => {
      if (!selectedProject) {
        setSubcodeBreakdown(null);
        setResourceSubcodeMatrix(null);
        setProjectMeta(null);
        return;
      }
      try {
        setSelectedLoading(true);
        const pr = encodeURIComponent(selectedProject.id);
        console.log(selectedProject);
        const res = await apiFetch(
          `/api/reports/project-subcodes?${selectedYear !== -1 ? "year=" + selectedYear + "&" : ""}project=${pr}`,
        );
        setSubcodeBreakdown(res && res.breakdown ? res.breakdown : null);
        const res2 = await apiFetch(`/api/reports/project-years?project=${pr}`);
        setProjectYears(res2 && res2.years ? res2.years : null);
        const res3 = await apiFetch(
          `/api/reports/project-resource-subcodes?${selectedYear !== -1 ? "year=" + selectedYear + "&" : ""}project=${pr}`,
        );
        setResourceSubcodeMatrix(res3 && res3.matrix ? res3 : null);
        try {
          const pm = await apiFetch(
            `/api/reports/project-meta-linked?${selectedYear !== -1 ? "year=" + selectedYear + "&" : ""}project=${pr}`,
          );
          console.log(pm);
          if (
            pm &&
            pm.projects &&
            Array.isArray(pm.projects) &&
            pm.projects.length > 0
          )
            setProjectMeta(pm.projects[0]);
          else if (pm && pm.project) setProjectMeta(pm.project);
          else setProjectMeta(null);
        } catch (e) {
          setProjectMeta(null);
        }
      } catch (e) {
        setSubcodeBreakdown(null);
        setResourceSubcodeMatrix(null);
      }
      setSelectedLoading(false);
    })();
  }, [selectedProject, year, selectedYear]);

  useEffect(() => {
    setSelectedYear(year);
  }, [selectedProject, year]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/projects");
        if (res && Array.isArray(res.projects))
          setProjects(
            res.projects.map((p) => ({ id: p.id, code: p.code, desc: p.desc })),
          );
        setProjectsMeta(res.projects);
      } catch (e) {}
    })();
  }, []);

  const formatGBP = (v) => {
    if (v === null || typeof v === "undefined" || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(n);
  };

  // Export the current view (table + charts + project meta) to a printable window.
  async function exportToPDF() {
    try {
      const baseTitle = `Project Overview ${
        selectedProject ? selectedProject.code : ""
      } ${year}`;
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
      const title = `${baseTitle} ${dateStr}`;

      function getCanvasFromChartRef(ref) {
        if (!ref || !ref.current) return null;
        const c = ref.current;
        return (
          c.canvas ||
          (c.chart && c.chart.canvas) ||
          (c.chartInstance && c.chartInstance.canvas) ||
          null
        );
      }

      const pieCanvas = getCanvasFromChartRef(pieRef);
      const barCanvas = getCanvasFromChartRef(barRef);
      const pieImg = pieCanvas ? pieCanvas.toDataURL("image/png") : null;
      const barImg = barCanvas ? barCanvas.toDataURL("image/png") : null;

      const filteredRows = (rows || []).filter(
        (r) =>
          (!selectedProject || r.project === selectedProject.code) &&
          /S&M|AC MGT/i.test(r.project),
      );
      const monthsHeader = (months || [])
        .map((m) => `<th style="border:1px solid #ddd;padding:6px;">${m}</th>`)
        .join("");
      const rowsHtml = (filteredRows || [])
        .map((r) => {
          const vals = (r.months || [])
            .map(
              (v) => `<td style="border:1px solid #eee;padding:6px;">${v}</td>`,
            )
            .join("");
          const totalHours = (r.months || []).reduce(
            (a, b) => a + Number(b || 0),
            0,
          );
          const totalDays = (totalHours / HOURS_PER_DAY).toFixed(2);
          return `<tr><td style="border:1px solid #eee;padding:6px;">${r.project}</td>${vals}<td style="border:1px solid #eee;padding:6px;">${totalDays}</td></tr>`;
        })
        .join("");

      let projectMetaHtml = "";
      if (projectMeta) {
        projectMetaHtml = `
          <div style="margin-top:12px;padding:10px;border:1px solid #eee;background:#fafafa;display:inline-block;">
            <div style="font-weight:700;margin-bottom:6px">Project details</div>
            <div>Estimated Time: ${
              projectMeta.Project_Est_Time ??
              projectMeta.project_est_time ??
              projectMeta.est_time ??
              "—"
            }</div>
            <div>Total Timesheet Days (${year}): ${
              typeof projectMeta.totalHours !== "undefined" &&
              projectMeta.totalHours !== null
                ? (projectMeta.totalHours / HOURS_PER_DAY).toFixed(2)
                : "—"
            }</div>
            <div>Project Value: ${formatGBP(
              projectMeta.Project_Value ??
                projectMeta.project_value ??
                projectMeta.value ??
                null,
            )}</div>
          </div>
        `;
      }

      const doc = window.open("", "_blank");
      if (!doc) throw new Error("Unable to open print window (popup blocked)");
      doc.document.write(`<!doctype html><html><head><title>${title}</title>`);
      try {
        doc.document.title = title;
      } catch (e) {}
      doc.document.write(
        `<style>body{font-family:Arial,Helvetica,sans-serif;padding:20px} table{border-collapse:collapse;width:100%} th,td{border:1px solid #ddd;padding:6px}</style>`,
      );
      doc.document.write(`</head><body>`);
      // Show a cleaner heading without the date (filename will include the date)
      doc.document.write(`<h2>${baseTitle}</h2>`);
      if (pieImg)
        doc.document.write(
          `<div><h3>Activity breakdown</h3><img src="${pieImg}" style="max-width:680px;width:100%;height:auto;"/></div>`,
        );
      if (barImg)
        doc.document.write(
          `<div><h3>Resource by Activity</h3><img src="${barImg}" style="max-width:680px;width:100%;height:auto;"/></div>`,
        );
      doc.document.write(projectMetaHtml);
      doc.document.write(`<h3 style="margin-top:12px;">Project table</h3>`);
      doc.document.write(
        `<table><thead><tr><th style="border:1px solid #ddd;padding:6px;text-align:left">Project</th>${monthsHeader}<th style="border:1px solid #ddd;padding:6px;">Total (days)</th></tr></thead><tbody>${rowsHtml}</tbody></table>`,
      );
      doc.document.write(`</body></html>`);
      doc.document.close();
      setTimeout(() => {
        try {
          //doc.focus();
          doc.print();
        } catch (e) {
          console.warn("Print failed:", e);
        }
        doc.close();
      }, 600);
    } catch (e) {
      console.error(e);
      alert("Export failed: " + (e && e.message ? e.message : String(e)));
    }
  }

  function exportToCSV() {
    const api = gridRef.current?.api;
    if (!api) return;

    const projectCode = selectedProject?.code ? `-${selectedProject.code}` : "";
    api.exportDataAsCsv({
      fileName: `client-support-${year}${projectCode}.csv`,
    });
  }

  const displayRows = useMemo(() => {
    return (rows || []).filter(
      (r) =>
        (!selectedProject || r.project === selectedProject.code) &&
        /S&M|AC MGT/i.test(r.project),
    );
  }, [rows, selectedProject]);

  const columnDefs = useMemo(() => {
    return [
      {
        field: "project",
        headerName: "Project",
        minWidth: 140,
        flex: 2,
        pinned: "left",
      },
      ...months.map((m, idx) => ({
        field: `month_${idx}`,
        headerName: m,
        minWidth: 72,
        flex: 1,
        type: "numericColumn",
        valueFormatter: (params) => Number(params.value || 0).toFixed(2),
      })),
      {
        field: "totalDays",
        headerName: "Total (days)",
        minWidth: 100,
        flex: 1,
        type: "numericColumn",
        valueFormatter: (params) => Number(params.value || 0).toFixed(2),
      },
    ];
  }, [months]);

  const rowData = useMemo(() => {
    return displayRows.map((r) => {
      const row = {
        project: r.project,
        id: r.id,
        totalDays:
          (r.months || []).reduce((a, b) => a + Number(b || 0), 0) /
          HOURS_PER_DAY,
      };
      (r.months || []).forEach((val, i) => {
        row[`month_${i}`] = Number(val || 0);
      });
      return row;
    });
  }, [displayRows]);

  return (
    <div style={{ padding: 20 }}>
      <h2>Client Support</h2>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label style={{ fontWeight: 600 }}>Year</label>
        <button onClick={() => setYear((y) => y - 1)}>-</button>
        <input
          type="number"
          value={year}
          onChange={(e) =>
            setYear(Number(e.target.value || new Date().getFullYear()))
          }
          style={{ width: 100 }}
        />
        <button onClick={() => setYear((y) => y + 1)}>+</button>
        <div style={{ marginLeft: 12 }}>
          <button onClick={() => fetchReport(year)}>Refresh</button>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={() => exportToPDF()}>Export to PDF</button>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={() => exportToCSV()}>Export CSV</button>
        </div>
        <div style={{ marginLeft: 12, minWidth: 260 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>Project (search)</div>
          <Typeahead
            items={projects.filter((v) =>
              rows.some((r) => r.project === v.code),
            )}
            value={selectedProject}
            onChange={(it) => setSelectedProject(it)}
            placeholder="Type project code or description"
            getItemKey={(it) => it.code}
            getItemLabel={(it) => `${it.code} — ${it.desc}`}
            renderItem={(it) => (
              <div>
                <strong style={{ marginRight: 8 }}>{it.code}</strong>
                <span>{it.desc}</span>
              </div>
            )}
          />
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <div style={{ color: "darkred" }}>{error}</div>
      ) : (
        <div style={{ overflowX: "visible" }}>
          {/* Chart: multi-line time series (months on X, hours on Y) */}
          <div style={{ maxWidth: 980, margin: "12px auto 20px" }}>
            {(() => {
              const displayRows = (rows || []).filter(
                (r) =>
                  (!selectedProject || r.project === selectedProject.code) &&
                  /S&M|AC MGT/i.test(r.project),
              );
              if (!displayRows || displayRows.length === 0) return null;

              // Aggregate by ProjectClientCode
              const clientMap = {};
              for (const r of displayRows) {
                const pj = (projectsMeta || []).find(
                  (p) =>
                    String(p.code || p.id || "").toLowerCase() ===
                    String(r.project || r.id || "").toLowerCase(),
                );
                const client = (
                  (pj &&
                    (pj.ProjectClientCode ||
                      pj.clientCode ||
                      pj.ClientCode ||
                      pj.ProjectClient ||
                      pj.client)) ||
                  r.project ||
                  "Unknown"
                ).toString();
                if (!clientMap[client]) {
                  const len =
                    months && months.length
                      ? months.length
                      : Array.isArray(r.months)
                        ? r.months.length
                        : 0;
                  clientMap[client] = {
                    client,
                    months: Array.from({ length: len }, () => 0),
                  };
                }
                const target = clientMap[client];
                if (Array.isArray(r.months)) {
                  for (let i = 0; i < r.months.length; i++) {
                    target.months[i] =
                      (target.months[i] || 0) + Number(r.months[i] || 0);
                  }
                }
              }

              const clients = Object.keys(clientMap).sort();

              // choose month indices that have data across clients
              let monthIndices = [];
              if (months && months.length) {
                for (let i = 0; i < months.length; i++) {
                  const has = clients.some(
                    (c) =>
                      Number(
                        (clientMap[c].months && clientMap[c].months[i]) || 0,
                      ) !== 0,
                  );
                  if (has) monthIndices.push(i);
                }
                if (monthIndices.length === 0)
                  monthIndices = months.map((_, i) => i);
              } else if (clients.length > 0) {
                const cnt = clientMap[clients[0]].months.length;
                for (let i = 0; i < cnt; i++) {
                  const has = clients.some(
                    (c) =>
                      Number(
                        (clientMap[c].months && clientMap[c].months[i]) || 0,
                      ) !== 0,
                  );
                  if (has) monthIndices.push(i);
                }
                if (monthIndices.length === 0)
                  monthIndices = Array.from({ length: cnt }, (_, i) => i);
              }

              const monthLabels = monthIndices.map((i) =>
                months && months[i] ? months[i] : `M${i + 1}`,
              );

              const datasets = clients.map((c) => ({
                label: c,
                data: monthIndices.map((i) =>
                  Number((clientMap[c].months && clientMap[c].months[i]) || 0),
                ),
                borderColor: getColorForKey(c),
                backgroundColor: getColorForKey(c),
                tension: 0.2,
                fill: false,
                pointRadius: 3,
              }));

              const data = { labels: monthLabels, datasets };
              const options = {
                plugins: { legend: { display: true, position: "bottom" } },
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: { title: { display: true, text: "Month" } },
                  y: {
                    beginAtZero: true,
                    title: { display: true, text: "Hours" },
                  },
                },
              };

              return (
                <div style={{ height: 300, marginBottom: 20, width: "100%" }}>
                  <Line ref={barRef} data={data} options={options} />
                </div>
              );
            })()}
          </div>

          <div style={{ width: "100%", overflowX: "auto" }}>
            <div
              className="ag-theme-balham client-support-grid"
              style={{ width: "100%" }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                domLayout="autoHeight"
                rowStyle={{ backgroundColor: "transparent" }}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  minWidth: 72,
                  cellStyle: { backgroundColor: "transparent" },
                }}
                onRowClicked={(event) => {
                  setSelectedProject(
                    projects.find((element) => element.id === event.data.id),
                  );
                }}
              />
            </div>
          </div>

          {/* Month selector: sits below the table but above the charts. */}
          {selectedProject && (
            <div style={{ marginTop: 12, marginBottom: 12 }}>
              <label style={{ fontWeight: 600, marginRight: 8 }}>Year</label>
              <select
                value={selectedYear || year}
                onChange={(e) =>
                  setSelectedYear(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="-1">All</option>
                {projectYears.map((year) => (
                  <option value={year}>{year}</option>
                ))}
              </select>
              <label style={{ marginLeft: 8, color: "darkred" }}>
                {selectedLoading ? "Loading..." : ""}
              </label>
            </div>
          )}

          {subcodeBreakdown &&
            subcodeBreakdown.length > 0 &&
            selectedProject && (
              <div
                style={{
                  display: "flex",
                  gap: 20,
                  flexWrap: "wrap",
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                <div
                  style={{
                    flex: "1 1 320px",
                    maxWidth: 600,
                    minWidth: 320,
                  }}
                >
                  <h3 style={{ textAlign: "center" }}>
                    Activity breakdown for {selectedProject.code}
                  </h3>
                  <div style={{ height: 240 }}>
                    <Pie
                      data={{
                        labels: subcodeBreakdown.map((s) => s.sub || "(none)"),
                        datasets: [
                          {
                            data: subcodeBreakdown.map((s) =>
                              Number(s.hours || 0),
                            ),
                            backgroundColor: subcodeBreakdown.map((s) =>
                              getColorForKey(s.sub),
                            ),
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        onHover: (evt, elements) => {
                          try {
                            if (elements && elements.length > 0)
                              evt.native.target.style.cursor = "pointer";
                            else evt.native.target.style.cursor = "default";
                          } catch (e) {}
                        },
                      }}
                      ref={pieRef}
                      onClick={async (evt, elements) => {
                        try {
                          let idx =
                            elements && elements.length
                              ? elements[0].index
                              : null;
                          if (
                            (idx === null || idx === undefined) &&
                            pieRef.current &&
                            evt &&
                            evt.native
                          ) {
                            try {
                              const arr =
                                pieRef.current.getElementsAtEventForMode(
                                  evt.native,
                                  "nearest",
                                  { intersect: true },
                                  true,
                                );
                              if (arr && arr.length) idx = arr[0].index;
                            } catch (e) {}
                          }
                          if (idx === null || idx === undefined) return;
                          const sub =
                            subcodeBreakdown &&
                            subcodeBreakdown[idx] &&
                            subcodeBreakdown[idx].id;
                          const pr = encodeURIComponent(selectedProject.id);
                          const qs = `project=${pr}${
                            sub ? "&sub=" + encodeURIComponent(sub) : ""
                          }&start=${selectedYear}-01-01&end=${selectedYear}-12-31`;
                          setDrillTitle(`${selectedProject.code} — ${sub}`);
                          setDrillOpen(true);
                          const rows = await apiFetch(`/api/timesheets?${qs}`);
                          setDrillRows(Array.isArray(rows) ? rows : []);
                        } catch (e) {}
                      }}
                      height={240}
                    />
                  </div>

                  {/* Activity / subcode buttons below the pie chart */}
                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {subcodeBreakdown.map((s, i) => {
                      const key = s.sub || "(none)";
                      return (
                        <button
                          key={s.id + "_" + i}
                          onClick={async () => {
                            try {
                              const sub = s.sub || "";
                              const pr = encodeURIComponent(selectedProject.id);
                              const qs = `project=${pr}&activity=${encodeURIComponent(
                                s.id,
                              )}&start=${selectedYear !== -1 ? selectedYear : Math.min(...projectYears)}-01-01&end=${selectedYear !== -1 ? selectedYear : Math.max(...projectYears)}-12-31`;
                              setDrillTitle(`${selectedProject.code} — ${sub}`);
                              setDrillOpen(true);
                              const rows = await apiFetch(
                                `/api/timesheets?${qs}`,
                              );
                              setDrillRows(Array.isArray(rows) ? rows : []);
                            } catch (e) {}
                          }}
                        >
                          {key}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {resourceSubcodeMatrix && resourceSubcodeMatrix.resources && (
                  <div
                    style={{
                      flex: "1 1 320px",
                      maxWidth: 600,
                      minWidth: 320,
                    }}
                  >
                    <h3 style={{ textAlign: "center" }}>
                      Resource by Activity
                    </h3>
                    <div style={{ height: 240 }}>
                      <Bar
                        data={{
                          labels: resourceSubcodeMatrix.resources.map(
                            (res) => res.code,
                          ),
                          datasets: resourceSubcodeMatrix.subCodes.map(
                            (sub) => ({
                              label: sub,
                              data:
                                resourceSubcodeMatrix.matrix[sub] ||
                                resourceSubcodeMatrix.resources.map(() => 0),
                              backgroundColor: getColorForKey(sub),
                            }),
                          ),
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            x: { stacked: true },
                            y: { stacked: true, beginAtZero: true },
                          },
                        }}
                        ref={barRef}
                        onClick={async (evt, elements) => {
                          try {
                            if (elements && elements.length) {
                              const el = elements[0];
                              const datasetIndex = el.datasetIndex;
                              const index = el.index;
                              const sub =
                                resourceSubcodeMatrix.subCodes[datasetIndex];
                              const resource =
                                resourceSubcodeMatrix.resources[index];
                              const pr = encodeURIComponent(selectedProject.id);
                              const qs = `project=${pr}&resource=${encodeURIComponent(resource)}&sub=${encodeURIComponent(sub)}&start=${selectedYear}-01-01&end=${selectedYear}-12-31`;
                              setDrillTitle(`${resource} — ${sub}`);
                              setDrillOpen(true);
                              const rows = await apiFetch(
                                `/api/timesheets?${qs}`,
                              );
                              setDrillRows(Array.isArray(rows) ? rows : []);
                              return;
                            }

                            let idx = null;
                            try {
                              const c = barRef && barRef.current;
                              const chart =
                                c && (c.chart || c.chartInstance || c);
                              if (chart && evt && evt.native) {
                                const native = evt.native;
                                if (
                                  typeof chart.getElementsAtEventForMode ===
                                  "function"
                                ) {
                                  const arr = chart.getElementsAtEventForMode(
                                    native,
                                    "index",
                                    { intersect: false },
                                    true,
                                  );
                                  if (arr && arr.length) idx = arr[0].index;
                                }
                                if (
                                  (idx === null || idx === undefined) &&
                                  chart.scales &&
                                  chart.scales.x &&
                                  typeof chart.scales.x.getValueForPixel ===
                                    "function"
                                ) {
                                  const rect =
                                    chart.canvas.getBoundingClientRect();
                                  const x = native.clientX - rect.left;
                                  const val =
                                    chart.scales.x.getValueForPixel(x);
                                  idx = Math.round(val);
                                }
                              }
                            } catch (e) {}

                            if (idx === null || idx === undefined) return;
                            const resource =
                              resourceSubcodeMatrix.resources[idx];
                            if (!resource) return;
                            const pr = encodeURIComponent(selectedProject.id);
                            const qs = `project=${pr}&start=${selectedYear}-01-01&end=${selectedYear}-12-31`;
                            setDrillTitle(resource);
                            setDrillOpen(true);
                            const rows = await apiFetch(
                              `/api/timesheets?${qs}`,
                            );
                            setDrillRows(Array.isArray(rows) ? rows : []);
                          } catch (e) {}
                        }}
                        height={240}
                      />
                    </div>

                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {resourceSubcodeMatrix.resources.map((res) => (
                        <button
                          key={res.id}
                          onClick={async () => {
                            try {
                              const pr = encodeURIComponent(selectedProject.id);
                              const qs = `project=${pr}&user=${encodeURIComponent(
                                res.id,
                              )}&start=${selectedYear !== -1 ? selectedYear : Math.min(...projectYears)}-01-01&end=${selectedYear !== -1 ? selectedYear : Math.max(...projectYears)}-12-31`;
                              setDrillTitle(res.code);
                              setDrillOpen(true);
                              const rows = await apiFetch(
                                `/api/timesheets?${qs}`,
                              );
                              setDrillRows(Array.isArray(rows) ? rows : []);
                            } catch (e) {}
                          }}
                        >
                          {res.code}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          {projectMeta && (
            <div style={{ width: "100%", marginTop: 12, textAlign: "center" }}>
              <div
                style={{
                  display: "inline-block",
                  textAlign: "left",
                  padding: 12,
                  border: "1px solid #eee",
                  borderRadius: 6,
                  background: "#fafafa",
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                  Project details
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Estimated Time
                    </div>
                    <div style={{ fontWeight: 700 }}>
                      {projectMeta.ProjectEstTime ??
                        projectMeta.project_est_time ??
                        projectMeta.est_time ??
                        "—"}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Total Timesheet Days ({year})
                    </div>
                    <div style={{ fontWeight: 700 }}>
                      {typeof projectMeta.totalHours !== "undefined" &&
                      projectMeta.totalHours !== null
                        ? (projectMeta.totalHours / HOURS_PER_DAY).toFixed(2)
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Project Value
                    </div>
                    <div style={{ fontWeight: 700 }}>
                      {formatGBP(
                        projectMeta.ProjectValue ??
                          projectMeta.project_value ??
                          projectMeta.value ??
                          null,
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Actual Project Cost
                    </div>
                    {typeof projectMeta.totalHours !== "undefined" &&
                    projectMeta.totalHours !== null ? (
                      (() => {
                        const days = projectMeta.totalHours / HOURS_PER_DAY;
                        const cost = days * 1500;
                        const projectVal =
                          (projectMeta.ProjectValue ??
                            projectMeta.project_value ??
                            projectMeta.value) ||
                          null;
                        let cls = "";
                        if (projectVal !== null && !isNaN(Number(projectVal))) {
                          cls =
                            cost <= Number(projectVal)
                              ? "project-cost--ok"
                              : "project-cost--over";
                        }
                        return (
                          <div className={cls} style={{ fontWeight: 700 }}>
                            {formatGBP(cost)}
                          </div>
                        );
                      })()
                    ) : (
                      <div style={{ fontWeight: 700 }}>-</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <button onClick={() => (onClose ? onClose() : null)}>Back</button>
      </div>

      {drillOpen && (
        <div
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setDrillOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              padding: 16,
              borderRadius: 6,
              minWidth: 600,
              maxHeight: "80%",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0 }}>{`Entries for ${drillTitle}`}</h3>
              <button onClick={() => setDrillOpen(false)}>Close</button>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>Date</th>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>
                    Resource
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>
                    Project
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>Sub</th>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: 6,
                      textAlign: "right",
                    }}
                  >
                    Hours
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>
                    Division
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody>
                {(drillRows || []).map((r, i) => (
                  <tr key={i}>
                    <td style={{ border: "1px solid #eee", padding: 6 }}>
                      {r.TimesheetDate
                        ? new Date(r.TimesheetDate).toLocaleDateString()
                        : ""}
                    </td>
                    <td style={{ border: "1px solid #eee", padding: 6 }}>
                      {r.UserCode}
                    </td>
                    <td style={{ border: "1px solid #eee", padding: 6 }}>
                      {r.ProjectCode}
                    </td>
                    <td style={{ border: "1px solid #eee", padding: 6 }}>
                      {r.ActivityCode}
                    </td>
                    <td
                      style={{
                        border: "1px solid #eee",
                        padding: 6,
                        textAlign: "right",
                      }}
                    >
                      {Number(r.TimesheetHours || 0).toFixed(2)}
                    </td>
                    <td style={{ border: "1px solid #eee", padding: 6 }}>
                      {r.TimesheetDivision}
                    </td>
                    <td
                      style={{
                        border: "1px solid #eee",
                        padding: 6,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {r.TimesheetNotes || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectOverview;
